// ================================================================
// 匯率管理頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：匯率設定與管理
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Edit,
  Refresh,
  Save,
  Cancel,
} from '@mui/icons-material';
import { getCurrencyRates, updateCurrencyRate, CurrencyRate } from '../../api/admin';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';

// ================================================================
// 主要組件
// ================================================================

const CurrencyManagementPage: React.FC = () => {
  const { admin } = useAdmin();

  // 狀態
  const [currencies, setCurrencies] = useState<CurrencyRate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 編輯狀態
  const [editDialog, setEditDialog] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<CurrencyRate | null>(null);
  const [editRate, setEditRate] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // 載入匯率列表
  const loadCurrencies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getCurrencyRates();
      if (response.success && response.data) {
        setCurrencies(response.data);
      } else {
        setError(response.message || '無法載入匯率資料');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrencies();
  }, [loadCurrencies]);

  // 開啟編輯對話框
  const handleEdit = (currency: CurrencyRate) => {
    setEditingCurrency(currency);
    setEditRate(currency.rate.toString());
    setEditActive(currency.is_active);
    setEditDialog(true);
  };

  // 儲存編輯
  const handleSave = async () => {
    if (!editingCurrency) return;

    const rate = parseFloat(editRate);
    if (isNaN(rate) || rate <= 0) {
      setError('請輸入有效的匯率（大於 0）');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await updateCurrencyRate(
        editingCurrency.currency_code,
        rate,
        editActive
      );
      if (response.success) {
        setSuccess(`${editingCurrency.currency_code} 匯率已更新為 ${rate}`);
        loadCurrencies();
        setEditDialog(false);
      } else {
        setError(response.message || '更新失敗');
      }
    } catch (err: any) {
      setError(err.message || '更新失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 貨幣符號對應
  const getCurrencySymbol = (code: string): string => {
    const symbols: { [key: string]: string } = {
      TWD: 'NT$',
      USD: '$',
      EUR: '€',
      JPY: '¥',
      CNY: '¥',
      GBP: '£',
      KRW: '₩',
      HKD: 'HK$',
      SGD: 'S$',
      AUD: 'A$',
    };
    return symbols[code] || code;
  };

  // 貨幣名稱對應
  const getCurrencyName = (code: string): string => {
    const names: { [key: string]: string } = {
      TWD: '新台幣',
      USD: '美元',
      EUR: '歐元',
      JPY: '日圓',
      CNY: '人民幣',
      GBP: '英鎊',
      KRW: '韓元',
      HKD: '港幣',
      SGD: '新加坡幣',
      AUD: '澳幣',
    };
    return names[code] || code;
  };

  // 檢查是否有編輯權限
  const canEdit = admin?.role === 'super_admin' || admin?.role === 'admin';

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            匯率管理
          </Typography>
          <Tooltip title="重新整理">
            <IconButton onClick={loadCurrencies} disabled={isLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 說明 */}
        <Alert severity="info" sx={{ mb: 3 }}>
          匯率以新台幣（TWD）為基準。例如：USD 匯率 0.032 表示 1 TWD = 0.032 USD。
        </Alert>

        {/* 成功訊息 */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 匯率列表 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>貨幣代碼</TableCell>
                  <TableCell>貨幣名稱</TableCell>
                  <TableCell>符號</TableCell>
                  <TableCell align="right">匯率</TableCell>
                  <TableCell align="center">狀態</TableCell>
                  <TableCell>更新時間</TableCell>
                  {canEdit && <TableCell align="right">操作</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : currencies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">沒有匯率資料</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currencies.map((currency) => (
                    <TableRow key={currency.currency_code} hover>
                      <TableCell>
                        <Typography fontWeight="bold">{currency.currency_code}</Typography>
                      </TableCell>
                      <TableCell>{getCurrencyName(currency.currency_code)}</TableCell>
                      <TableCell>{getCurrencySymbol(currency.currency_code)}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" fontFamily="monospace">
                          {currency.rate.toFixed(6)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={currency.is_active ? '啟用' : '停用'}
                          color={currency.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(currency.updated_at).toLocaleString('zh-TW')}
                      </TableCell>
                      {canEdit && (
                        <TableCell align="right">
                          <Tooltip title="編輯">
                            <IconButton onClick={() => handleEdit(currency)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 編輯對話框 */}
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            編輯匯率 - {editingCurrency?.currency_code} ({getCurrencyName(editingCurrency?.currency_code || '')})
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="匯率"
                type="number"
                value={editRate}
                onChange={(e) => setEditRate(e.target.value)}
                margin="normal"
                inputProps={{
                  step: '0.000001',
                  min: '0',
                }}
                helperText="相對於 TWD 的匯率（例如：1 TWD = ? USD）"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editActive}
                    onChange={(e) => setEditActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="啟用此貨幣"
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditDialog(false)}
              startIcon={<Cancel />}
              disabled={isProcessing}
            >
              取消
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<Save />}
              disabled={isProcessing || !editRate}
            >
              {isProcessing ? <CircularProgress size={20} /> : '儲存'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default CurrencyManagementPage;
